import { toError } from '@fruitful/fruitils';
import {
    type AnalyticsMembershipEvent,
    AnalyticsUserEvent,
} from '@fruitful/types';
import { Platform } from 'react-native';

import { IS_LOCAL, IS_TEST } from '../config/FruitfulEnvironment';
import CustomerIOFacade from '../events/customerio/CustomerIOFacade';
import {
    isGoogleTagManagerLoaded,
    pushGoogleTagManagerEvent,
    sendGoogleTagEvent,
} from '../events/growth/googleEventTracking';
import MixpanelFacade from '../events/mixpanel/MixpanelFacade';
import {
    logFruitSliceEventToBackend,
    logToBackend,
} from '../events/utils/LogToBackend';
import FetchError from '../http/FetchError';
import type { User } from '../user/types/User';
import DatadogFacade from './datadog/DatadogFacade';
import LogRocketFacade from './log-rocket/LogRocketFacade';
import {
    enrichSentryScope,
    type SentryErrorData,
} from './sentry/enrichSentryScope';
import SentryFacade from './sentry/SentryFacade';
import StatsigFacade from './statsig/StatsigFacade';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ErrorData = Record<string, any>;

export const captureMessage = (message: string) => {
    if (DatadogFacade.isEnabled()) {
        void DatadogFacade.Datadog.info(message);
    }

    if (LogRocketFacade.isEnabled()) {
        LogRocketFacade.log(message);
    }

    if (IS_LOCAL) {
        console.info(message);
    }
};

export const captureWarning = (message: string, exception?: unknown) => {
    const error = exception == null ? undefined : toError(exception, message);

    if (DatadogFacade.isEnabled()) {
        void DatadogFacade.Datadog.warn(message, error);
    }

    if (LogRocketFacade.isEnabled()) {
        LogRocketFacade.captureWarning(message, error);
    }

    if (IS_LOCAL) {
        console.warn(message, error);
    }
};

export const captureDebug = (message: string) => {
    if (DatadogFacade.isEnabled()) {
        void DatadogFacade.Datadog.debug(message);
    }

    if (IS_LOCAL) {
        console.debug(message);
    }
};

const getSentryErrorData = (
    exception: Error,
    data?: ErrorData,
): SentryErrorData | undefined => {
    if (!(exception instanceof FetchError)) {
        return data;
    }

    const existingTags =
        data != null &&
        typeof data.tags === 'object' &&
        data.tags != null &&
        !Array.isArray(data.tags)
            ? (data.tags as Record<string, string | number | boolean>)
            : {};

    return {
        ...data,
        tags: {
            ...existingTags,
            endpoint: exception.endpoint,
            method: exception.method,
            status: exception.status,
        },
    };
};

export const captureException = (exception: unknown, data?: ErrorData) => {
    const error = toError(exception);
    const sentryData = getSentryErrorData(error, data);

    if (LogRocketFacade.isEnabled()) {
        LogRocketFacade.captureException(error, {
            extra: data,
        });
    }

    if (SentryFacade.isEnabled()) {
        SentryFacade.Sentry.withScope((scope) => {
            enrichSentryScope(scope, sentryData);
            SentryFacade.Sentry.captureException(error);
        });
    }

    if (DatadogFacade.isEnabled()) {
        DatadogFacade.logError(error, data);
    }

    if (IS_LOCAL) {
        console.error(error, data);
    }
};

export const handleUncaughtException = (error: unknown, stackTrace: string) => {
    captureException(error, {
        stackTrace,
    });
};

export const track = (
    event: AnalyticsUserEvent,
    data?: Record<string, string | number | boolean>,
    /** applies only for Mixpanel events */
    sendImmediately?: boolean,
) => {
    LogRocketFacade.track(event as string, data);
    MixpanelFacade.track(event, data, sendImmediately);
    void CustomerIOFacade.track(event, data);
    void logFruitSliceEventToBackend(event, data).catch((error: unknown) => {
        captureWarning('Unable to send fruit slice event to backend', error);
    });

    if (
        IS_TEST /* ignore logging when running tests */ ||
        !(
            event === AnalyticsUserEvent.OPEN_MONEY_MAP_WORKFLOW ||
            event === AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_SUCCESS ||
            event.startsWith('Onboarding') ||
            event === AnalyticsUserEvent.FF_DEBUGGING
        )
    ) {
        // 2025-09-16 [ENG-5031] Only capture events above in Statsig to start
        return;
    }

    StatsigFacade.logEvent(event, data);
};

export const trackGrowthMarketingEvent = (
    event: string,
    data?: Record<string, string | number | boolean>,
) => {
    if (Platform.OS !== 'web') return;

    // We expect GTM to only be loaded for signup flow routes(where it is necessary)
    // Otherwise GTM adds too much overhead and unnecessary 3rd party scripts
    if (isGoogleTagManagerLoaded()) {
        pushGoogleTagManagerEvent(event, data);
        return;
    }

    // Pages without that container send the event through gtag.js.
    // Note: this path does not send events to all growth marketing integrations facilitated by GTM.
    // If necessary, we would have to add other specific integrations to call here, e.g. meta pixel.
    sendGoogleTagEvent(event, data);
};

export const trackView = (page: string) => {
    LogRocketFacade.tagPage(page);
    MixpanelFacade.track(AnalyticsUserEvent.VIEW, {
        view: page,
    });
    void CustomerIOFacade.screen(page);
    void logFruitSliceEventToBackend(AnalyticsUserEvent.VIEW, {
        view: page,
    }).catch((error: unknown) => {
        captureWarning('Unable to send fruit slice event to backend', error);
    });
};

export const trackForMembership = (
    event: AnalyticsMembershipEvent,
    data?: Record<string, string | number | boolean>,
) => {
    // Wouldn't make sense (or be possible) to log events with LogRocket for a membership, but we should still capture an event for the current user
    LogRocketFacade.track(event as string, data);

    void logToBackend(event, data, 'membership').catch((error: unknown) => {
        captureWarning('Unable to send event log to backend', error);
    });
};

export const reset = (): void => {
    DatadogFacade.clearUser();
    SentryFacade.clearUser();
    MixpanelFacade.reset();
    CustomerIOFacade.reset();
};

export const identifyUser = (user: User) => {
    LogRocketFacade.identifyUser(user);
    SentryFacade.identifyUser(user);
    DatadogFacade.identifyUser(user);
    MixpanelFacade.identifyUser(user);
    void CustomerIOFacade.identifyUser(user);
};

export const identifyVisitor = (visitorId: string) => {
    LogRocketFacade.identifyVisitor(visitorId);
    SentryFacade.identifyVisitor(visitorId);
    DatadogFacade.identifyVisitor(visitorId);
    MixpanelFacade.identifyVisitor(visitorId);
    void CustomerIOFacade.identifyVisitor(visitorId);
};
